'use client'

import type { Cliente } from '@/payload-types'

import React, { useActionState, useState } from 'react'

import { aggiornaProfilo, eliminaAccount, type StatoForm } from '../actions'
import { ErroreCampo, Esito } from './Feedback'

type Risposta = StatoForm & { risposta?: number }

/** The cliente's own data as form values (the same keys `aggiornaProfilo` reads). */
const valoriCliente = (cliente: Cliente): Record<string, string> => ({
  tipoCliente: cliente.tipoCliente,
  nome: cliente.nome,
  cognome: cliente.cognome,
  ragioneSociale: cliente.ragioneSociale ?? '',
  partitaIva: cliente.partitaIva ?? '',
  codiceFiscale: cliente.codiceFiscale ?? '',
  pec: cliente.pec ?? '',
  codiceSdi: cliente.codiceSdi ?? '',
  telefono: cliente.telefono ?? '',
  marketing: cliente.consensoMarketing?.accettato ? 'on' : '',
})

/** Profile form of `/account`: everything but the email (the login) and the privacy consent. */
export const FormProfilo: React.FC<{ cliente: Cliente }> = ({ cliente }) => {
  // Keyed on the answers, as the registration form: React resets a form after each action,
  // and the <select> would go back to its first-render option while the company fields stay.
  const [stato, azione, inCorso] = useActionState(
    async (prev: Risposta, formData: FormData) => ({
      ...(await aggiornaProfilo(prev, formData)),
      risposta: (prev.risposta ?? 0) + 1,
    }),
    { stato: 'idle', risposta: 0 } as Risposta,
  )
  const valori = stato.valori ?? valoriCliente(cliente)
  const [tipo, setTipo] = useState(valori.tipoCliente ?? 'privato')
  const azienda = tipo === 'azienda'
  const campi = stato.campi ?? {}

  return (
    <form action={azione} aria-label="I tuoi dati" className="accountform" key={stato.risposta}>
      <label>
        Email
        <input disabled readOnly type="email" value={cliente.email} />
      </label>
      <small>L’email è il tuo accesso: per cambiarla scrivici.</small>
      <label>
        Tipo di cliente
        <select
          defaultValue={valori.tipoCliente ?? 'privato'}
          name="tipoCliente"
          onChange={(e) => setTipo(e.target.value)}
        >
          <option value="privato">Privato</option>
          <option value="azienda">Professionista / Azienda</option>
        </select>
      </label>
      <div className="accountcolumns">
        <label>
          Nome
          <input
            autoComplete="given-name"
            defaultValue={valori.nome}
            maxLength={80}
            name="nome"
            required
          />
          <ErroreCampo messaggio={campi.nome} />
        </label>
        <label>
          Cognome
          <input
            autoComplete="family-name"
            defaultValue={valori.cognome}
            maxLength={80}
            name="cognome"
            required
          />
          <ErroreCampo messaggio={campi.cognome} />
        </label>
      </div>
      {azienda && (
        <div>
          <label>
            Ragione sociale
            <input
              autoComplete="organization"
              defaultValue={valori.ragioneSociale}
              maxLength={160}
              name="ragioneSociale"
              required
            />
            <ErroreCampo messaggio={campi.ragioneSociale} />
          </label>
          <div className="accountcolumns">
            <label>
              Partita IVA
              <input
                defaultValue={valori.partitaIva}
                inputMode="numeric"
                maxLength={16}
                name="partitaIva"
                required
              />
              <ErroreCampo messaggio={campi.partitaIva} />
            </label>
            <label>
              Codice SDI <span>(facoltativo)</span>
              <input
                defaultValue={valori.codiceSdi}
                maxLength={7}
                name="codiceSdi"
                style={{ textTransform: 'uppercase' }}
              />
              <ErroreCampo messaggio={campi.codiceSdi} />
            </label>
          </div>
          <label>
            PEC <span>(facoltativa)</span>
            <input defaultValue={valori.pec} name="pec" type="email" />
            <ErroreCampo messaggio={campi.pec} />
          </label>
        </div>
      )}
      <div className="accountcolumns">
        <label>
          Codice fiscale <span>(facoltativo)</span>
          <input
            defaultValue={valori.codiceFiscale}
            maxLength={16}
            name="codiceFiscale"
            style={{ textTransform: 'uppercase' }}
          />
          <ErroreCampo messaggio={campi.codiceFiscale} />
        </label>
        <label>
          Telefono <span>(facoltativo)</span>
          <input autoComplete="tel" defaultValue={valori.telefono} name="telefono" type="tel" />
          <ErroreCampo messaggio={campi.telefono} />
        </label>
      </div>
      <label className="accountconsent">
        <input defaultChecked={valori.marketing === 'on'} name="marketing" type="checkbox" /> Voglio
        ricevere offerte e novità via email <span>(facoltativo, revocabile)</span>
      </label>
      <button className="primary" disabled={inCorso} type="submit">
        {inCorso ? 'Salvataggio in corso…' : 'Salva i dati'}
      </button>
      {stato.messaggio && <Esito stato={stato} />}
    </form>
  )
}

/** Account deletion: password + checkbox, then a last browser confirmation. */
export const FormEliminaAccount: React.FC = () => {
  const [stato, azione, inCorso] = useActionState(eliminaAccount, { stato: 'idle' } as StatoForm)
  const campi = stato.campi ?? {}

  return (
    <details className="accountform" open={stato.stato === 'errore'}>
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Elimina il tuo account</summary>
      <form
        action={azione}
        aria-label="Elimina account"
        className="accountform"
        onSubmit={(e) => {
          if (!window.confirm('Eliminare definitivamente il tuo account? Non si può annullare.')) {
            e.preventDefault()
          }
        }}
      >
        <p className="modalnote">
          Cancelliamo i tuoi dati e la rubrica degli indirizzi. Gli ordini già fatti restano nel
          nostro archivio, come previsto dagli obblighi fiscali, ma non li vedrai più qui: per
          seguirli usa i link nelle email di conferma.
        </p>
        <label>
          Password
          <input autoComplete="current-password" name="password" required type="password" />
          <ErroreCampo messaggio={campi.password} />
        </label>
        <label className="accountconsent">
          <input name="conferma" required type="checkbox" /> Ho capito che l’eliminazione è
          definitiva
          <ErroreCampo messaggio={campi.conferma} />
        </label>
        <button className="secondary" disabled={inCorso} type="submit">
          {inCorso ? 'Eliminazione in corso…' : 'Elimina definitivamente l’account'}
        </button>
        {stato.messaggio && <Esito stato={stato} />}
      </form>
    </details>
  )
}
